import ActionRow from '@/components/ActionRow';
import CategoriesMenu from '@/components/CategoriesMenu';
import EmptyActionList from '@/components/EmptyActionList';
import Input from '@/components/Input';
import ProjectsMenu from '@/components/ProjectsMenu';
import PurposeSelect from '@/components/PurposeSelect';
import StyledModal from '@/components/StyledModal';
import { COMPLETE } from '@/constants/action';
import { INSERT_BLOCK_ONE } from '@/gql/block';
import { CreateBlockResponse } from '@/gql/block/types';
import { keys } from '@/gql/global/keys';
import { useCMActions } from '@/services/action/hooks';
import { useAddActionsToBlock } from '@/services/block/hooks';
import { useCategories } from '@/services/categories/hooks';
import { fetchData } from '@/services/graphql';
import useIsDailyPlanPage from '@/services/plans/hooks/useIsDailyPlanPage';
import { useWeeklyPlanId } from '@/services/plans/hooks/useWeeklyPlan';
import { useProjects } from '@/services/project/hooks';
import { useActionModal } from '@/stores/useActionModal';
import { IconPlus } from '@/theme/icons';
import { Action } from '@/types/actions';
import { Block } from '@/types/block';
import { ProjectType } from '@/types/project';
import { getDailyActions, getWeeklyActions } from '@/utils/action';
import { formatStringToDate, utcToLocalDate } from '@/utils/calendar';
import { getCategoryById } from '@/utils/category';
import { validateRecurrence } from '@/utils/events';
import rem from '@/utils/rem';
import { refetchQueries } from '@/utils/tanStackQuery';
import { trackActionAddedToBlock, trackBlockCreated } from '@/utils/tracking';
import {
  Button,
  Checkbox,
  FormLabel,
  HStack,
  IconButton,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Text,
  Tooltip,
  VStack,
  useBoolean,
  useToast,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { TFunction } from 'i18next';
import { isEmpty, sortBy } from 'lodash';
import { ChangeEvent, Children, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { z } from 'zod';

type Props = {
  actionId?: string;
  isOpen: boolean;
  scheduledDate?: Date;
  categoryId?: string;
  projectId?: string;
  readyOnlyCategory?: boolean;
  readOnlyProject?: boolean;
  result?: string;
  filterPeriod?: 'day' | 'week' | 'month' | 'year';
  onSuccess?: (block: Block) => void;
  onClose: () => void;
};

type FormData = {
  result: string;
  purpose: string;
};

export function getBlockFormSchema(t: TFunction) {
  return z.object({
    result: z
      .string()
      .trim()
      .min(1, { message: t('validation.resultRequired') })
      .max(512, { message: t('validation.resultMaxLength') }),
    purpose: z
      .string()
      .trim()
      .min(1, { message: t('validation.purposeRequired') })
      .max(10000, { message: t('validation.purposeMaxLength') }),
  });
}

function CreateBlockModal({
  actionId,
  isOpen,
  scheduledDate,
  categoryId,
  projectId: tempProjectId,
  readyOnlyCategory = false,
  readOnlyProject = false,
  result = '',
  filterPeriod,
  onSuccess,
  onClose,
}: Props) {
  const [isSubmitting, setIsSubmitting] = useBoolean();
  const toast = useToast();
  const isDailyPlanPage = useIsDailyPlanPage();
  const [selectedCategoryId, setSelectedCategoryId] = useState(categoryId);

  useEffect(() => {
    setSelectedCategoryId(categoryId);
  }, [categoryId]);

  const [isCheckAll, setIsCheckAll] = useState(false);
  const [selectedActions, setSelectedActions] = useState<Action[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectType | null>(null);

  const { data: weeklyPlanId } = useWeeklyPlanId(scheduledDate ?? new Date());
  const { data: categories } = useCategories();
  const { data: projects } = useProjects();
  const { data: actions, isLoading: actionsLoading } = useCMActions(selectedCategoryId);

  const createBlockWithMultipleActions = useAddActionsToBlock();
  const { setActionModalOpen } = useActionModal();

  useEffect(() => {
    setSelectedProject(projects?.project.find((p: ProjectType) => p.id === tempProjectId) ?? null);
  }, [tempProjectId, projects]);

  const selectedCategory = useMemo(() => {
    if (selectedCategoryId) {
      return getCategoryById(categories?.category ?? [], selectedCategoryId);
    }
    const undefinedCategory = categories?.category?.find((category) => category.name.toLowerCase() === 'uncategorized');
    setSelectedCategoryId(undefinedCategory?.id);
    return undefinedCategory;
  }, [categories, selectedCategoryId]);

  const actionsNotAlreadyInBlock = useMemo(() => {
    let filteredActions = [];

    const incompleteActions = actions?.action
      .filter((a) => !a.isCanceled)
      .filter((action) => action.progressStatus !== COMPLETE);

    // Custom filtering logic for CategorySpotlight periods
    if (filterPeriod && scheduledDate) {
      const filterByPeriod = (action: Action) => {
        // Only consider scheduledDate and weeklyPlan
        let actionDate: Date | null = null;

        if (action.scheduledDate) {
          actionDate = utcToLocalDate(action.scheduledDate, action.scheduledTime, action.timezone, action.gmtOffset);
        } else if (action.weekly_plan?.dateFrom) {
          actionDate = formatStringToDate(action.weekly_plan.dateFrom);
        }

        // Skip actions without scheduledDate or weeklyPlan
        if (!actionDate) return false;

        if (filterPeriod === 'day') {
          // For day, check if the action is scheduled for the same day
          return (
            actionDate.getFullYear() === scheduledDate.getFullYear() &&
            actionDate.getMonth() === scheduledDate.getMonth() &&
            actionDate.getDate() === scheduledDate.getDate()
          );
        } else if (filterPeriod === 'week') {
          // For week, use the existing weekly logic
          return action.weeklyPlanId === String(weeklyPlanId);
        } else if (filterPeriod === 'month') {
          return (
            actionDate.getFullYear() === scheduledDate.getFullYear() &&
            actionDate.getMonth() === scheduledDate.getMonth()
          );
        } else if (filterPeriod === 'year') {
          return actionDate.getFullYear() === scheduledDate.getFullYear();
        }

        return false;
      };

      filteredActions = incompleteActions?.filter(filterByPeriod) ?? [];
    }
    // Original logic for other parts of the app
    else if (scheduledDate && isDailyPlanPage) {
      filteredActions = getDailyActions(incompleteActions ?? [], scheduledDate);
    } else if (scheduledDate && weeklyPlanId) {
      filteredActions = getWeeklyActions(incompleteActions ?? [], weeklyPlanId);
    } else {
      filteredActions = incompleteActions ?? [];
    }

    if (selectedProject?.id) {
      filteredActions = filteredActions.filter((action) => action.projectId === selectedProject.id);
    } else {
      filteredActions = filteredActions.filter((action) => !action.projectId);
    }

    filteredActions = filteredActions.filter((action) => !action.blockId);
    filteredActions = filteredActions.filter((action) => !validateRecurrence(action.event?.recurrence));

    // if there is a pre-selected action, it should be at the top of the list
    return sortBy(filteredActions, ({ id }) => (id === actionId ? 0 : 1));
  }, [scheduledDate, weeklyPlanId, isDailyPlanPage, actions, selectedProject?.id, actionId, filterPeriod]);

  const selectedAction = useMemo(
    () => actionsNotAlreadyInBlock?.find((action) => action.id === actionId),
    [actionsNotAlreadyInBlock, actionId],
  );

  const { t } = useTranslation('CreateBlockModal');

  const BlockFormSchema = getBlockFormSchema(t);

  const {
    control,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors, isDirty, isValid },
  } = useForm<FormData>({
    defaultValues: {
      result,
      purpose: '',
    },
    resolver: zodResolver(BlockFormSchema),
    mode: 'onChange',
  });

  const createBlock = useMutation({
    mutationFn: (variables: Omit<Block, 'category'>) => fetchData<CreateBlockResponse>(INSERT_BLOCK_ONE, variables),

    onSuccess: (data, { projectId }) => {
      const cacheKeys = [];
      cacheKeys.push(keys.blocks.all.queryKey);
      projectId && cacheKeys.push(keys.project.detail(projectId).queryKey);
      refetchQueries(cacheKeys);

      onSuccess && onSuccess(data.insertBlockOne);
      onCloseModal();
      trackBlockCreated();

      toast({
        title: t('toasts.blockCreated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const createBlockFromActions = useMutation({
    mutationFn: (variables: Omit<Block, 'category'>) => fetchData<CreateBlockResponse>(INSERT_BLOCK_ONE, variables),

    onSuccess: (data) => {
      createBlockWithMultipleActions.mutate({
        actionIds: selectedActions.map((action) => action.id),
        blockId: data.insertBlockOne.id,
        blockOrders: selectedActions.map((_, index) => index + 1),
        projectId: data.insertBlockOne.projectId,
        categoryId: data.insertBlockOne.categoryId,
      });

      onSuccess && onSuccess(data.insertBlockOne);

      trackBlockCreated();
      selectedActions.forEach((action) => trackActionAddedToBlock(!!action.dateOfStarring));
      onCloseModal();

      toast({
        title: t('toasts.blockCreated'),
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    },
  });

  const onSubmit = useCallback(
    ({ result, purpose }: FormData) => {
      if (!selectedCategory) {
        return;
      }

      setIsSubmitting.on();

      if (!isEmpty(selectedActions)) {
        createBlockFromActions.mutate({
          id: uuidv4(),
          result,
          purpose,
          categoryId: selectedCategory.id,
          scheduledDate: isDailyPlanPage && scheduledDate ? scheduledDate : null,
          weeklyPlanId: scheduledDate ? String(weeklyPlanId) : null,
          projectId: selectedProject?.id ?? undefined,
          isCompleted: false,
        });

        setIsSubmitting.off();
      } else {
        createBlock.mutate({
          id: uuidv4(),
          result,
          purpose,
          categoryId: selectedCategory.id,
          scheduledDate: isDailyPlanPage && scheduledDate ? scheduledDate : null,
          weeklyPlanId: scheduledDate ? String(weeklyPlanId) : null,
          projectId: selectedProject?.id ?? undefined,
          isCompleted: false,
        });

        setIsSubmitting.off();
      }
    },
    [
      setIsSubmitting,
      selectedActions,
      createBlockFromActions,
      scheduledDate,
      weeklyPlanId,
      selectedCategory,
      createBlock,
      isDailyPlanPage,
      selectedProject?.id,
    ],
  );

  const onCloseModal = useCallback(() => {
    reset();
    setSelectedActions([]);
    setIsCheckAll(false);
    setSelectedProject(null);
    setSelectedCategoryId(categoryId);
    onClose();
  }, [onClose, reset, categoryId]);

  const handleSelect = useCallback(
    (e: ChangeEvent<HTMLInputElement>, action: Action) => {
      const { checked } = e.target;
      const nextActions = checked
        ? [...selectedActions, action]
        : selectedActions.filter((item) => item?.id !== action?.id);
      setSelectedActions(nextActions);
    },
    [selectedActions],
  );

  const handleSelectAll = useCallback(() => {
    setIsCheckAll(!isCheckAll);

    if (isCheckAll) {
      setSelectedActions([]);
      setIsCheckAll(false);
    } else {
      setSelectedActions([...actionsNotAlreadyInBlock]);
      setIsCheckAll(true);
    }
  }, [actionsNotAlreadyInBlock, isCheckAll]);

  useEffect(() => {
    if (actionsNotAlreadyInBlock && selectedActions.length === actionsNotAlreadyInBlock.length) {
      setIsCheckAll(true);
    } else {
      setIsCheckAll(false);
    }
  }, [actionsNotAlreadyInBlock, selectedActions]);

  useEffect(() => {
    if (selectedAction && !validateRecurrence(selectedAction.event?.recurrence)) {
      setSelectedActions([selectedAction]);
    } else if (selectedAction) {
      setSelectedActions([]);
    }
  }, [selectedAction]);

  // unselect all actions when project changes
  useEffect(() => {
    setSelectedActions([]);
  }, [selectedProject?.id]);

  useEffect(() => {
    if (isOpen) {
      // Does not work without setTimeout
      // must be removed after focus trap is enabled
      // (lookup the comments in the codebase by "RRI-2918")
      const timeoutId = setTimeout(() => setFocus('result'), 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, setFocus]);

  return (
    <StyledModal size={'5xl'} isOpen={isOpen} onClose={onCloseModal}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader justifyContent="space-between" display="flex">
          {t('title.createNewBlock')}
          <HStack alignItems="center">
            {categories?.category?.length && (
              <CategoriesMenu
                shouldCreateCategory={false}
                readonly={readyOnlyCategory}
                category={selectedCategory}
                onSelectCategory={(category) => {
                  setSelectedCategoryId(category.id);
                  setSelectedActions([]);
                  setIsCheckAll(false);
                }}
              />
            )}
            <ProjectsMenu
              variant="bordered"
              categoryId={selectedCategory?.id}
              project={selectedProject}
              setSelectedProject={setSelectedProject}
              readonly={readOnlyProject}
            />
          </HStack>
        </ModalHeader>
        <ModalBody>
          <VStack alignItems="normal" width="full" spacing={rem(24)}>
            <Controller
              name="result"
              control={control}
              render={({ field: { ref, value, onChange } }) => (
                <Input
                  ref={ref}
                  label={t('labels.result')}
                  placeholder={t('labels.resultPlaceholder')}
                  type="text"
                  autoComplete="off"
                  value={value}
                  onChange={onChange}
                  error={errors?.result?.message}
                  _placeholder={{
                    color: 'text-tertiary',
                    opacity: 0.3,
                    fontSize: '3xl',
                    fontWeight: 500,
                  }}
                  fontSize="3xl"
                  fontWeight={500}
                  paddingBottom={rem(16)}
                  errorMessageProps={{
                    color: 'red.500',
                    textStyle: 'large',
                  }}
                />
              )}
            />

            <Controller
              name="purpose"
              control={control}
              render={({ field }) => (
                <PurposeSelect
                  {...field}
                  category={selectedCategory}
                  error={errors?.purpose?.message}
                  placeholder={t('labels.purposePlaceholder')}
                />
              )}
            />

            <>
              <VStack alignItems="start" gap={rem(24)}>
                <HStack width="full">
                  <FormLabel margin={0} color="text-tertiary" fontSize="sm" fontWeight="900" textTransform="uppercase">
                    {t('labels.addActionsToBlock')}
                  </FormLabel>
                  <Tooltip label={t('tooltips.addAction')} placement="top">
                    <IconButton
                      minWidth={rem(30)}
                      height={rem(28)}
                      marginLeft="auto"
                      aria-label={t('ariaLabels.addAction')}
                      icon={<IconPlus height={rem(10)} width={rem(10)} />}
                      onClick={() => {
                        setActionModalOpen(true, {
                          categoryId: selectedCategory?.id,
                          projectId: selectedProject?.id,
                          showProjectSelector: !!selectedProject?.id,
                          readonlyProject: !!selectedProject?.id,
                          readonlyCategory: true,
                          readonlyTooltipLabel: t('tooltips.readonlyCategory'),
                          disallowActionRecurrence: true,
                          onCreateAction: (action) => {
                            if (validateRecurrence(action.event?.recurrence)) {
                              return;
                            }
                            setSelectedActions([...selectedActions, action]);
                          },
                          location: 'block modal',
                        });
                      }}
                      variant="secondary"
                    />
                  </Tooltip>
                  <Button
                    width="fit-content"
                    height="full"
                    isDisabled={isEmpty(actionsNotAlreadyInBlock)}
                    onClick={handleSelectAll}
                    paddingInline={rem(8)}
                    paddingY={rem(5)}
                    size="sm"
                    variant="secondary"
                  >
                    <Text textStyle="small">{t(`buttons.${isCheckAll ? 'unselectAll' : 'selectAll'}`)}</Text>
                  </Button>
                </HStack>
                <Text color="text-tertiary" fontSize="sm">
                  {t('labels.actionsFilterHint')}
                </Text>
                {!actionsLoading && (
                  <VStack overflowY="scroll" width="full" maxHeight={rem(200)}>
                    {!isEmpty(actionsNotAlreadyInBlock) ? (
                      Children.toArray(
                        actionsNotAlreadyInBlock?.map((action) => {
                          return (
                            <HStack width="full">
                              <ActionRow
                                action={action}
                                backgroundColor="background-primary"
                                readOnly
                                hideButtons
                                progressStatusButtonDisabled
                                minWidth={0}
                              />
                              <Checkbox
                                isChecked={selectedActions.some((obj) => obj.id === action.id)}
                                onChange={(e) => handleSelect(e, action)}
                              />
                            </HStack>
                          );
                        }),
                      )
                    ) : (
                      <EmptyActionList
                        title={t('labels.noCurrentActions')}
                        message={t('labels.clickToAddAction')}
                        motivational="small"
                        backgroundColor="background-primary"
                      />
                    )}
                  </VStack>
                )}
              </VStack>
            </>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button onClick={onCloseModal} size="lg" variant="secondary">
            {t('buttons.cancel')}
          </Button>
          <Button
            isDisabled={!isDirty || !isValid || createBlock.isLoading || isEmpty(selectedActions)}
            isLoading={isSubmitting}
            size="lg"
            type="submit"
            variant="primary"
          >
            {t('buttons.createBlock')}
          </Button>
        </ModalFooter>
      </form>
    </StyledModal>
  );
}

export default CreateBlockModal;
